"use client"

import { createContext, useContext } from "react"
import type { DisplayRates } from "@lib/util/display-currency"

/**
 * The customer's chosen display currency and the rates to convert with, for
 * client components. Redmine #166532.
 *
 * Both are resolved once per request on the server — the currency from the
 * middleware header or cookie, the rates from the backend — and passed down,
 * rather than each component fetching for itself. Client components cannot use
 * next/headers, and totals/price components are overwhelmingly client components
 * because of the interactive checkout.
 *
 * A null currency means "no preference": render the charge currency alone, with
 * no conversion and no approximation marker.
 *
 * Null RATES mean "do not convert" even when a currency IS chosen. That happens
 * when the backend is unreachable or its rates are stale, and showing the native
 * price alone is the correct response — a missing second figure is honest, a
 * wrong one is not.
 */
type DisplayCurrencyValue = {
  currency: string | null
  rates: DisplayRates | null
}

const DisplayCurrencyContext = createContext<DisplayCurrencyValue>({
  currency: null,
  rates: null,
})

export function DisplayCurrencyProvider({
  value,
  rates,
  children,
}: {
  value: string | null
  rates: DisplayRates | null
  children: React.ReactNode
}) {
  return (
    <DisplayCurrencyContext.Provider value={{ currency: value, rates }}>
      {children}
    </DisplayCurrencyContext.Provider>
  )
}

/** The chosen display currency, or null for "no preference". */
export function useDisplayCurrency(): string | null {
  return useContext(DisplayCurrencyContext).currency
}

/** The rates to convert with, or null meaning "do not convert". */
export function useDisplayRates(): DisplayRates | null {
  return useContext(DisplayCurrencyContext).rates
}
